/**
 * Who is signed in, and the way out.
 *
 * Signed out it is a sign-in button carrying the current location as `returnTo`, so a visitor who
 * signs in from the course they were reading lands back on it. Signed in it is a menu with the
 * name, the role and the admin area when the role allows it.
 */
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { Avatar, Button, Menu, VisuallyHidden, type MenuItem } from '@catalog/ui';

import { useAuth } from '../auth/auth-context.js';
import { AppLink } from './AppLink.js';

export function UserMenu() {
  const { user, isAdmin, isPending, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [signingOut, setSigningOut] = useState(false);

  if (isPending) {
    // A skeleton here would be a flash of grey in the header on every load. The space is held by
    // the surrounding layout instead.
    return <VisuallyHidden>Checking your session</VisuallyHidden>;
  }

  if (user === null) {
    const returnTo = `${location.pathname}${location.search}`;

    return (
      <AppLink to={`/login?returnTo=${encodeURIComponent(returnTo)}`} variant="subtle">
        Sign in
      </AppLink>
    );
  }

  const name = user.displayName ?? user.email;

  const items: MenuItem[] = [
    { id: 'identity', label: user.email, disabled: true },
    {
      id: 'role',
      label: isAdmin ? 'Administrator' : 'Member',
      disabled: true,
    },
    ...(isAdmin
      ? [
          {
            id: 'admin',
            label: 'Admin area',
            onSelect: () => {
              void navigate('/admin');
            },
          },
        ]
      : []),
    {
      id: 'sign-out',
      label: signingOut ? 'Signing out…' : 'Sign out',
      tone: 'danger' as const,
      disabled: signingOut,
      onSelect: () => {
        setSigningOut(true);
        void signOut()
          .then(async () => navigate('/courses'))
          /**
           * The sign-out is applied locally whatever the request did — the provider clears the
           * cache in `onSettled` — so there is nothing to report and nothing to retry. Resetting
           * the flag is all that is left in case the component is still mounted.
           */
          .finally(() => {
            setSigningOut(false);
          });
      },
    },
  ];

  return (
    <Menu
      label={`Account menu for ${name}`}
      items={items}
      trigger={
        <Button variant="ghost" size="sm">
          <Avatar name={name} size="sm" />
          <span>{name}</span>
        </Button>
      }
    />
  );
}
